import {Box} from '@mui/material';
import {PorjectSectionBaseProp} from './ProjectDrawer';
import styles from '../../../styles/components/clientdetails/projectDrawerStyle.module.scss';
import {useEffect, useState} from 'react';
import SectionDetailsHeader from './SectionDetailsHeader';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import {t} from 'i18next';

const MAX_CHARACTER_LENGTH = 500;

type ProjectDescriptionProps = {
  projectDescription: string;
  isEdit?: boolean;
  isEditButtonVisible?: boolean;
} & PorjectSectionBaseProp;

function ProjectDescription({
  projectDescription,
  isEdit = false,
  onEditClickHandler,
  onCancelClickHandler,
  onSaveChangeHandler,
  isEditButtonVisible = true,
}: ProjectDescriptionProps) {
  const {showModal} = useModal();
  const [descriptionValue, setDescriptionValue] =
    useState<string>(projectDescription);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] =
    useState<boolean>(true);
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);

  const saveChangeHandler = async () => {
    if (!hasValidationError) {
      const isSavedSuccessfullly = await onSaveChangeHandler({
        description: descriptionValue.trim(),
      });

      setIsSaveButtonDisabled(isSavedSuccessfullly);
    }
  };

  const editClickHandler = () => {
    onEditClickHandler({projectDescription: true});
  };

  const descriptionChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescriptionValue(e.target.value);
    if (e.target.value.trim().length > MAX_CHARACTER_LENGTH) {
      setIsSaveButtonDisabled(true);
      setHasValidationError(true);
    } else if (hasLocalChanges(e.target.value)) {
      setIsSaveButtonDisabled(false);
      setHasValidationError(false);
    } else {
      setIsSaveButtonDisabled(true);
      setHasValidationError(false);
    }
  };

  const hasLocalChanges = (currentValue: string) => {
    if (currentValue.trim() != projectDescription.trim()) {
      return true;
    } else {
      return false;
    }
  };

  const cancelClickHandler = () => {
    if (hasLocalChanges(descriptionValue)) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => {
          onCancelClickHandler({businessStructure: false});
        },
      });
    } else {
      onCancelClickHandler({businessStructure: false});
    }
  };

  useEffect(() => {
    if (isEdit) {
      setDescriptionValue(projectDescription);
      setHasValidationError(false);
    }
  }, [isEdit]);
  const editMode = (
    <Box
      className={`${styles.projectDrawerProjectDescriptionDetailsEditContainer}`}
    >
      <SectionDetailsHeader
        headerText={t('clientDetailsPage.projectDrawer.projectDescription')}
        onCancelHandler={cancelClickHandler}
        onEditHandler={editClickHandler}
        onSaveChangesHandler={saveChangeHandler}
        isEdit={isEdit}
        isEditButtonVisible={false}
        isSaveButtonDisabled={isSaveButtonDisabled}
      />

      <Box>
        <AurTextField
          error={hasValidationError}
          value={descriptionValue}
          onChange={descriptionChangeHandler}
          variant="filled"
          fullWidth
          label={t('clientDetailsPage.projectDrawer.projectDescription')}
          multiline
          maxRows={6}
          ariaLabel={t('clientDetailsPage.projectDrawer.projectDescription')}
          helperText={
            hasValidationError
              ? `${t('clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText')}`
              : `${t('clientDetailsPage.projectDrawer.charLimitValidHelperText')}`
          }
          className={`${styles.customMultilineTextField} aur-description-textfield`}
        />
      </Box>
    </Box>
  );
  const viewMode = (
    <>
      <SectionDetailsHeader
        headerText={t(`clientDetailsPage.projectDrawer.projectDescription`)}
        onCancelHandler={cancelClickHandler}
        onEditHandler={editClickHandler}
        onSaveChangesHandler={saveChangeHandler}
        isEdit={isEdit}
        isEditButtonVisible={isEditButtonVisible}
      />
      <Box className={`${styles.projectDescriptionViewModeTextContainer}`}>
        {projectDescription}
      </Box>
    </>
  );
  return (
    <Box>
      <Box
        className={`${styles.projectDrawerProjectDescriptionDetailsContainer}`}
      >
        {isEdit ? editMode : viewMode}
      </Box>
    </Box>
  );
}

export default ProjectDescription;
